import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, dashboardService } from '../services/auth';
import { CiScoreBadge } from '../components/CiScoreBadge';
import CIScoreExplainer from '../components/CIScoreExplainer';
import AIRecommendations from '../components/AIRecommendations';
import { DealGrade, CIScoreWithGrade } from '../components/DealGrade';
import { AIGlow, AIBadge, AICard } from '../components/AIGlow';
import NextBestAction from '../components/NextBestAction';
import { ComingSoonBadge, FeatureCardComingSoon } from '../components/ComingSoonBadge';
import MerchantHeader from '../components/MerchantHeader';
import IntegrationStatusWidget from '../components/IntegrationStatusWidget';
import MarginGuardHero from '../components/MarginGuardHero';
import OnboardingModal from '../components/OnboardingModal';
import OnboardingJourney from '../components/OnboardingJourney';
import UpgradeBanner, { LimitReachedBanner } from '../components/UpgradeBanner';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import apiClient from '../services/api';
import DealPauseButton from '../components/DealPauseButton';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const loadDeals = async () => {
    try { const r = await dashboardService.getDeals(); setDeals(r.deals || []); } catch (e) { console.error('Failed to load deals:', e); }
  };
  const [chartData, setChartData] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showCIExplainer, setShowCIExplainer] = useState(false);
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [showAdjustPriceModal, setShowAdjustPriceModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [optimizingProduct, setOptimizingProduct] = useState(null);
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [newOptimizedPrice, setNewOptimizedPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Onboarding & Upgrade states
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [subscriptionLimits, setSubscriptionLimits] = useState(null);
  
  // Next Best Actions state
  const [nextBestActions, setNextBestActions] = useState([]);
  const [completedActions, setCompletedActions] = useState(new Set());
  const [loadingActions, setLoadingActions] = useState(true);
  
  // Shopify & Slack connection state
  const [isShopifyConnected, setIsShopifyConnected] = useState(false);
  const [isSlackConnected, setIsSlackConnected] = useState(false);

  useEffect(() => {
    loadDashboardData();
    loadOnboardingStatus();
    checkIntegrationStatus();
  }, []);

  const loadOnboardingStatus = async () => {
    try {
      const response = await apiClient.get('/api/auth/merchant/onboarding-status');
      setOnboardingStatus(response.data);
      setShowOnboarding(response.data.show_onboarding_modal);
      
      // Also load subscription limits
      const limitsResponse = await apiClient.get('/api/subscription/limits');
      setSubscriptionLimits(limitsResponse.data);
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
    }
  };

  const checkIntegrationStatus = async () => {
    try {
      const response = await apiClient.get('/api/integration/status');
      setIsShopifyConnected(response.data?.shopify?.connected || false);
      setIsSlackConnected(response.data?.slack?.connected || false);
    } catch (error) {
      console.error('Failed to check integration status:', error);
      // Fallback: try the old shopify status endpoint
      try {
        const shopifyResponse = await apiClient.get('/api/shopify/status');
        setIsShopifyConnected(shopifyResponse.data?.is_connected || false);
      } catch {
        // F-M02-4 (fix): was setIsShopifyConnected(true) with comment "Default to true to show the Margin Guard hero with data",
        // which showed Connected when both status calls failed. Unknown status is shown as not connected. Restore: never.
        setIsShopifyConnected(false);
      }
    }
  };

  const handleConnectShopify = () => {
    // Navigate to Shopify OAuth flow or integrations page
    navigate('/dashboard/integrations');
  };

  const handleOnboardingComplete = async (plan) => {
    try {
      await apiClient.post('/api/auth/merchant/complete-onboarding', { plan });
      setShowOnboarding(false);
      await loadDashboardData();
      await loadOnboardingStatus();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const handleUpgrade = () => {
    navigate('/?upgrade=true#pricing');
  };

  const loadDashboardData = async () => {
    try {
      const [profileData, statsData, productsData, chartResult, channelsData] = await Promise.all([
        authService.getProfile(),
        dashboardService.getStats(),
        dashboardService.getProducts(),
        dashboardService.getChartData(),
        dashboardService.getDistributionChannels()
      ]);

      setUser({
        ...profileData.member,
        is_superadmin: profileData.is_superadmin
      });
      setStats(statsData);
      setProducts(productsData.products);
      loadDeals();
      setChartData(chartResult.data);
      setChannels(channelsData.channels);
      setLoading(false);
      
      // Generate Next Best Actions based on products
      generateNextBestActions(productsData.products);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      navigate('/login');
    }
  };

  // Generate Next Best Actions based on products without deals
  const generateNextBestActions = (productsList) => {
    setLoadingActions(true);
    
    // Filter products that don't have active deals and aren't in completed actions
    const productsWithoutDeals = productsList.filter(p => {
      const hasNoDeal = !p.has_active_deal;
      const notCompleted = !completedActions.has(p._id);
      return hasNoDeal && notCompleted;
    });

    // Sort by potential - low CI score products have more room to improve
    const sortedProducts = [...productsWithoutDeals].sort((a, b) => {
      const aScore = a.ci_score || 500;
      const bScore = b.ci_score || 500;
      return aScore - bScore; // Lower scores first (more potential)
    });

    // Generate actions for top products
    const actions = sortedProducts.slice(0, 4).map((product, index) => {
      const ciScore = product.ci_score || 500;
      // F-M02-4 (fix): removed invented numbers. Original:
      //   const potentialScore = Math.min(950, ciScore + 200 + Math.floor(Math.random() * 100));
      //   const estimatedRevenue = Math.floor(150 + Math.random() * 400);
      // Restore only with a measured or clearly labeled rule-based source (see /api recommendations estimate_basis).
      const ciLabel = product.ci_score ?? 'not scored';
      
      // Vary action types based on CI score
      let actionType, actionTitle, actionDesc, actionButton;
      
      if (ciScore < 500) {
        actionType = 'create_deal';
        actionTitle = `Create deal for: ${product.title}`;
        actionDesc = `Low CI score (${ciLabel}). Creating a deal may improve visibility.`;
        actionButton = 'Create Deal';
      } else if (ciScore < 700) {
        actionType = 'optimize_price';
        actionTitle = `Optimize price on: ${product.title}`;
        actionDesc = `Current CI score ${ciLabel}. A price change may help.`;
        actionButton = 'Optimize';
      } else {
        actionType = 'adjust_pricing';
        actionTitle = `Adjust pricing on: ${product.title}`;
        actionDesc = `Fine-tune for maximum conversions (CI: ${ciScore})`;
        actionButton = 'Adjust Pricing';
      }

      return {
        id: product._id,
        type: actionType,
        priority: index === 0 ? 'high' : index < 2 ? 'medium' : 'low',
        iconType: index === 0 ? 'trending' : index === 1 ? 'dollar' : 'alert',
        title: actionTitle,
        description: actionDesc,
        action: actionButton,
        productId: product._id,
        product: product,
        currentPrice: product.price || 0,
        estimatedRevenue: null, // F-M02-4: was a random "+$X/week"; unknown is null
      };
    });

    setNextBestActions(actions);
    setLoadingActions(false);
  };

  // Mark an action as completed and refresh
  const markActionCompleted = (actionId) => {
    setCompletedActions(prev => new Set([...prev, actionId]));
    
    // Remove from current actions with animation
    setNextBestActions(prev => prev.map(action => 
      action.id === actionId 
        ? { ...action, completed: true }
        : action
    ));

    // After animation, regenerate actions
    setTimeout(() => {
      setNextBestActions(prev => prev.filter(action => action.id !== actionId));
      // Generate new action if we have more products
      generateNextBestActions(products);
    }, 500);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    sessionStorage.clear();
    navigate('/login');
  };

  // Handler for header "Create Deal" button
  const handleCreateDealFromHeader = () => {
    if (products.length > 0) {
      setSelectedProduct(null); // Will show product dropdown
      setShowDealModal(true);
    } else {
      toast.error('No products available', {
        description: 'Connect Shopify or add demo products to get started',
        action: {
          label: 'Add Demo Products',
          onClick: () => seedDemoProducts()
        }
      });
    }
  };

  // Seed demo products for testing
  const seedDemoProducts = async () => {
    try {
      const response = await apiClient.post('/api/dashboard/seed-demo-products');
      toast.success('Demo products created!', {
        description: `Created ${response.data.products_created} products and ${response.data.deals_created} deals`
      });
      // Refresh the page to load new products
      window.location.reload();
    } catch (error) {
      if (error.response?.data?.message === 'Demo products already exist') {
        toast.info('Demo products already exist');
        window.location.reload();
      } else {
        toast.error('Failed to create demo products', {
          description: error.response?.data?.detail || 'Please try again'
        });
      }
    }
  };

  const handleNextBestAction = (action) => {
    // Store the action ID to mark as completed after successful action
    const actionId = action.id;
    
    switch (action.type) {
      case 'create_deal':
        // Find the product and open deal creation modal
        const product = action.product || products.find(p => p._id === action.productId);
        if (product) {
          setSelectedProduct({ ...product, _actionId: actionId });
          setShowDealModal(true);
        } else {
          // If product not found (default actions), just open modal with product dropdown
          if (products.length > 0) {
            setSelectedProduct(null);
            setShowDealModal(true);
          } else {
            toast.error('No products available', {
              description: 'Connect Shopify or add demo products to get started',
              action: {
                label: 'Add Demo Products',
                onClick: () => seedDemoProducts()
              }
            });
          }
        }
        break;
      
      case 'adjust_pricing':
        // Open adjust price modal with slider
        const prodToAdjust = action.product || products.find(p => p._id === action.productId);
        if (prodToAdjust) {
          setAdjustingProduct({ ...prodToAdjust, _actionId: actionId });
          setShowAdjustPriceModal(true);
        } else if (action.dealId) {
          navigate(`/dashboard/deals?edit=${action.dealId}`);
        } else if (products.length > 0) {
          setAdjustingProduct({ ...products[0], _actionId: actionId });
          setShowAdjustPriceModal(true);
        } else {
          toast.error('No products available', {
            description: 'Connect Shopify or add demo products to get started',
            action: {
              label: 'Add Demo Products',
              onClick: () => seedDemoProducts()
            }
          });
        }
        break;
      
      case 'optimize_price':
        // Open optimize modal with +$5/-$5 buttons
        const prodToOptimize = action.product || products.find(p => p._id === action.productId);
        if (prodToOptimize) {
          setOptimizingProduct({ ...prodToOptimize, _actionId: actionId });
          setNewOptimizedPrice(prodToOptimize.price || 0);
          setShowOptimizeModal(true);
        } else if (products.length > 0) {
          const firstProd = products[0];
          setOptimizingProduct({ ...firstProd, _actionId: actionId });
          setNewOptimizedPrice(firstProd.price || 0);
          setShowOptimizeModal(true);
        } else {
          toast.error('No products available', {
            description: 'Connect Shopify or add demo products to get started',
            action: {
              label: 'Add Demo Products',
              onClick: () => seedDemoProducts()
            }
          });
        }
        break;
      
      default:
        // For other actions (like creator_network), navigate to link if provided
        if (action.link) {
          navigate(action.link);
        }
        break;
    }
  };

  const handleCreateDeal = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Store actionId before clearing selectedProduct
    const actionId = selectedProduct?._actionId;
    const productId = selectedProduct?._id;
    
    try {
      const newDeal = await dashboardService.createDeal({
        product_id: productId,
        discount_type: formData.get('discount_type'),
        discount_value: parseFloat(formData.get('discount_value')),
        coupon_code: formData.get('coupon_code') || null,
        end_date: formData.get('end_date') ? new Date(formData.get('end_date')).toISOString() : null
      });
      
      setShowDealModal(false);
      setSelectedProduct(null);
      
      // Mark the NBA action as completed if it came from NBA
      if (actionId) {
        markActionCompleted(actionId);
      }
      
      // Show success toast
      toast.success('Deal created successfully!', {
        description: 'Action completed! New recommendations loading...',
        duration: 4000,
      });
      
      // Refresh products to update has_active_deal status
      const productsData = await dashboardService.getProducts(50, 0);
      setProducts(productsData.products);
      
    } catch (error) {
      console.error('Failed to create deal:', error);
      toast.error('Failed to create deal', {
        description: error.response?.data?.detail || 'Please try again',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      {/* Onboarding Modal for new merchants */}
      {showOnboarding && (
        <OnboardingModal
          user={user}
          onComplete={handleOnboardingComplete}
          onSkip={() => handleOnboardingComplete('free')}
        />
      )}

      {/* New Professional Header */}
      <MerchantHeader user={user} onLogout={handleLogout} onCreateDeal={handleCreateDealFromHeader} isSuperadmin={user?.is_superadmin} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* 7-Day Onboarding Journey */}
        <OnboardingJourney />

        {/* Upgrade Banner for FREE plan users */}
        {onboardingStatus?.show_upgrade_banner && (
          <UpgradeBanner
            plan={onboardingStatus.current_plan}
            daysOnFreePlan={onboardingStatus.days_since_signup}
            onDismiss={() => {}}
          />
        )}

        {/* Limit Reached Banners */}
        {subscriptionLimits?.products && !subscriptionLimits.products.allowed && (
          <LimitReachedBanner
            limitType="product"
            current={subscriptionLimits.products.current}
            limit={subscriptionLimits.products.limit}
            onUpgrade={handleUpgrade}
          />
        )}
        {subscriptionLimits?.deals_this_month && !subscriptionLimits.deals_this_month.allowed && (
          <LimitReachedBanner
            limitType="deal"
            current={subscriptionLimits.deals_this_month.current}
            limit={subscriptionLimits.deals_this_month.limit}
            onUpgrade={handleUpgrade}
          />
        )}

        {/* HERO: Margin Guard - The Weekly Decision Surface */}
        <MarginGuardHero 
          isShopifyConnected={isShopifyConnected}
          isSlackConnected={isSlackConnected}
          onConnectShopify={handleConnectShopify}
        />

        {/* Secondary Panel: Next Best Action (Pricing Optimization) */}
        <div className="mb-8">
          <NextBestAction 
            actions={nextBestActions}
            loading={loading || loadingActions} 
            onActionClick={handleNextBestAction}
          />
          {/* DISPLACED 2026-11-01: 'All caught up / completed all recommended actions' claimed completion when no actions ever existed (zero-data merchant). NextBestAction now renders its own honest empty state. Restore check: only if actions carry a real completed-count from the API. */}
          {false && nextBestActions.length === 0 && !loadingActions && !loading && (
            <div className="mt-4 p-4 rounded-lg border border-[#3EF1C7]/30 bg-[#3EF1C7]/5 text-center">
              <p className="text-[#3EF1C7] font-medium">🎉 All caught up!</p>
              <p className="text-sm text-[#9CB0C7] mt-1">You've completed all recommended actions. New recommendations will appear as opportunities arise.</p>
            </div>
          )}
        </div>

        {/* Integration Status + Stats Row */}
        {/* Hide Integration Status when Shopify not connected (Margin Guard hero already has CTA) */}
        <div className={`grid grid-cols-1 ${isShopifyConnected ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6 mb-8`}>
          {/* Integration Status Widget - only show when Shopify IS connected */}
          {isShopifyConnected && (
            <IntegrationStatusWidget className="lg:col-span-1" />
          )}

          {/* Stats Cards */}
          <div className={`${isShopifyConnected ? 'lg:col-span-4' : 'lg:col-span-4'} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6`}>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Total Products</p>
              <div className="w-8 h-8 rounded-full bg-[#7A5CFF]/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-[#7A5CFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold text-white tabular-nums">{stats?.total_products || 0}</p>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Active Deals</p>
              <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-[#3EF1C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold text-white tabular-nums">{stats?.active_deals || 0}</p>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Avg CI Score</p>
              <div className="w-8 h-8 rounded-full bg-[#8CF78E]/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-[#8CF78E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-3xl font-bold text-white tabular-nums">{stats?.avg_ci_score ?? 'no data'}</p>
              {stats?.avg_ci_score != null && <DealGrade score={stats.avg_ci_score} showLabel={false} size="sm" />}
            </div>
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#9CB0C7]">Revenue Lift</p>
              <div className="w-8 h-8 rounded-full bg-[#FFC857]/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-[#FFC857]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#8CF78E] tabular-nums">{stats?.revenue_lift ?? '—'}</p>
          </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* AI Recommendations */}
          <div className="lg:col-span-1">
            <AIRecommendations />
          </div>

          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)] lg:col-span-1">
            <h3 className="text-lg font-semibold text-white mb-4">Distribution Channels</h3>
            {channels.length === 0 && (
              <p className="text-sm text-[#9CB0C7] mb-2" data-testid="channels-empty">No channel data measured yet.</p>
            )}
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={channels}>
                <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
                <XAxis dataKey="name" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                <YAxis stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
                <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} labelStyle={{color: '#9CB0C7'}} />
                <Bar dataKey="conversions" fill="#7A5CFF" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Chart */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 shadow-[0_0_0_1px_rgba(62,241,199,0.06)] mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Deal Performance (30 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorDeals" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3EF1C7" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3EF1C7" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#22304A" />
              <XAxis dataKey="date" stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#9CB0C7" tick={{fill: '#9CB0C7', fontSize: 12}} />
              <Tooltip contentStyle={{background: '#0E1426', border: '1px solid #22304A', borderRadius: '8px'}} labelStyle={{color: '#9CB0C7'}} />
              <Area type="monotone" dataKey="conversions" stroke="#3EF1C7" fillOpacity={1} fill="url(#colorDeals)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Products Table */}
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden shadow-[0_0_0_1px_rgba(62,241,199,0.06)]">
          <div className="p-6 border-b border-[#22304A]">
            <h3 className="text-lg font-semibold text-white">Products</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#111A2E]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">CI Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">Inventory</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#22304A]">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-[#1A2437] flex items-center justify-center">
                          <svg className="w-8 h-8 text-[#9CB0C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-1">No Products Yet</h4>
                          <p className="text-sm text-[#9CB0C7] max-w-md">
                            Connect your Shopify store to sync products, or add demo products to explore the platform.
                          </p>
                        </div>
                        <div className="flex gap-3 mt-2">
                          <button
                            onClick={seedDemoProducts}
                            className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white font-medium hover:bg-[#6B4DE6] transition-colors"
                            data-testid="add-demo-products-btn"
                          >
                            Add Demo Products
                          </button>
                          <button
                            onClick={() => navigate('/dashboard/settings/integrations')}
                            className="px-4 py-2 rounded-lg border border-[#22304A] text-white font-medium hover:bg-[#1A2437] transition-colors"
                            data-testid="connect-shopify-btn"
                          >
                            Connect Shopify
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : products.map((product) => (
                  <tr key={product._id} className="hover:bg-[#111A2E]/40 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-[#1A2437] flex items-center justify-center">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.title} className="h-10 w-10 rounded-lg object-cover" />
                          ) : (
                            <svg className="w-5 h-5 text-[#9CB0C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                          )}
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => navigate(`/dashboard/product/${product._id}`)}
                            className="text-sm font-medium text-white hover:text-[#3EF1C7] transition-colors text-left"
                          >
                            {product.title}
                          </button>
                          {product.sku && <div className="text-xs text-[#9CB0C7]">SKU: {product.sku}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-md bg-[#1A2437] text-[#9CB0C7]">{product.category}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white tabular-nums">${product.price.toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <CiScoreBadge score={product.ci_score} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#9CB0C7] tabular-nums">{product.inventory || 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setSelectedProduct(product);
                          setShowDealModal(true);
                        }}
                        className="px-3 py-1.5 rounded-lg text-[#3EF1C7] hover:bg-[#3EF1C7]/10 transition-colors"
                        data-testid="create-deal-button"
                      >
                        Create Deal
                      </button>
                      {(() => {
                        // Explicit deal identity: one control per live deal of this product (never "latest wins").
                        const pd = deals.filter((d) => String(d.product_id) === String(product._id) && d.status !== 'retired' && d.status !== 'expired');
                        return pd.map((d) => (
                          <DealPauseButton
                            key={d._id}
                            deal={d}
                            suffix={pd.length > 1 ? ` (${d.deal_price || d.price ? '$' + (d.deal_price || d.price) : '#' + String(d._id).slice(-4)})` : ''}
                            onChanged={loadDeals}
                          />
                        ));
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Deal Creation Modal */}
      {showDealModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}}>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-lg shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.75)]">
            <div className="p-6 border-b border-[#22304A]">
              <h3 className="text-xl font-semibold text-white">Create Deal</h3>
              {selectedProduct && <p className="text-sm text-[#9CB0C7] mt-1">for {selectedProduct.title}</p>}
            </div>
            <form onSubmit={handleCreateDeal} className="p-6 space-y-4">
              {/* Product Info - shows when product is pre-selected from Next Best Action */}
              {selectedProduct ? (
                <div className="p-4 rounded-lg bg-[#111A2E] border border-[#22304A]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#9CB0C7] uppercase tracking-wide">Selected Product</p>
                      <p className="text-white font-medium mt-1">{selectedProduct.title}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#9CB0C7] uppercase tracking-wide">Current Price</p>
                      <p className="text-2xl font-bold text-[#3EF1C7]">${(selectedProduct.price || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  {selectedProduct.ci_score && (
                    <div className="mt-3 pt-3 border-t border-[#22304A]">
                      <p className="text-xs text-[#9CB0C7]">Current CI Score: <span className="text-white font-medium">{selectedProduct.ci_score}</span></p>
                    </div>
                  )}
                </div>
              ) : (
                /* Product Selector - shows when no product is pre-selected */
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Select Product</label>
                  <select 
                    name="product_id" 
                    required 
                    className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60"
                    onChange={(e) => {
                      const prod = products.find(p => p._id === e.target.value);
                      if (prod) setSelectedProduct(prod);
                    }}
                  >
                    <option value="">Choose a product...</option>
                    {products.map(p => (
                      <option key={p._id} value={p._id}>{p.title} - ${p.price?.toFixed(2)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Discount Type</label>
                <select name="discount_type" required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60">
                  <option value="percentage">Percentage Off</option>
                  <option value="fixed">Fixed Amount Off</option>
                  <option value="bogo">Buy One Get One</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Discount Value</label>
                <input type="number" name="discount_value" step="0.01" required className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" placeholder="e.g., 20 for 20%" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Coupon Code (Optional)</label>
                <input type="text" name="coupon_code" className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" placeholder="SAVE20" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">End Date (Optional)</label>
                <input type="date" name="end_date" className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60" />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => {setShowDealModal(false); setSelectedProduct(null);}} className="flex-1 px-4 py-2.5 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors" data-testid="cancel-deal-button">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white transition-colors" style={{background: '#7A5CFF'}} data-testid="submit-deal-button">
                  Create Deal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Optimize Price Modal - With +$5/-$5 Buttons */}
      {showOptimizeModal && optimizingProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}} data-testid="optimize-modal">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-md shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.75)]">
            <div className="p-6 border-b border-[#22304A]">
              <h3 className="text-xl font-semibold text-white">Optimize Price</h3>
              <p className="text-sm text-[#9CB0C7] mt-1">{optimizingProduct.title}</p>
            </div>
            
            <div className="p-6">
              {/* Price Summary */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-[#111A2E] rounded-lg p-3 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Initial Price</p>
                  <p className="text-lg font-bold text-white">${(optimizingProduct.msrp || optimizingProduct.price || 0).toFixed(2)}</p>
                </div>
                <div className="bg-[#111A2E] rounded-lg p-3 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Current Price</p>
                  <p className="text-lg font-bold text-[#FFC857]">${(optimizingProduct.price || 0).toFixed(2)}</p>
                </div>
                <div className="bg-[#3EF1C7]/10 rounded-lg p-3 border border-[#3EF1C7]/30">
                  <p className="text-xs text-[#3EF1C7] mb-1">New Price</p>
                  <p className="text-lg font-bold text-[#3EF1C7]">${newOptimizedPrice.toFixed(2)}</p>
                </div>
              </div>

              {/* Quick Adjust Buttons */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-3">Quick Adjust</label>
                <div className="flex gap-3 justify-center">
                  <button 
                    onClick={() => setNewOptimizedPrice(prev => Math.max(0, prev - 5))}
                    className="px-6 py-3 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 font-bold hover:bg-red-500/30 transition-colors"
                    data-testid="reduce-5-btn"
                  >
                    − $5
                  </button>
                  <button 
                    onClick={() => setNewOptimizedPrice(prev => Math.max(0, prev - 1))}
                    className="px-5 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 font-medium hover:bg-red-500/20 transition-colors"
                    data-testid="reduce-1-btn"
                  >
                    − $1
                  </button>
                  <button 
                    onClick={() => setNewOptimizedPrice(prev => prev + 1)}
                    className="px-5 py-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-300 font-medium hover:bg-green-500/20 transition-colors"
                    data-testid="increase-1-btn"
                  >
                    + $1
                  </button>
                  <button 
                    onClick={() => setNewOptimizedPrice(prev => prev + 5)}
                    className="px-6 py-3 rounded-lg bg-green-500/20 border border-green-500/40 text-green-400 font-bold hover:bg-green-500/30 transition-colors"
                    data-testid="increase-5-btn"
                  >
                    + $5
                  </button>
                </div>
              </div>

              {/* Manual Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Or enter exact price</label>
                <div className="relative max-w-xs mx-auto">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CB0C7]">$</span>
                  <input
                    type="number"
                    value={newOptimizedPrice.toFixed(2)}
                    onChange={(e) => setNewOptimizedPrice(parseFloat(e.target.value) || 0)}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white text-lg font-bold text-center focus:outline-none focus:border-[#3EF1C7]"
                    data-testid="optimize-price-input"
                  />
                </div>
                {newOptimizedPrice !== (optimizingProduct.price || 0) && (
                  <p className="text-center text-sm mt-2">
                    {newOptimizedPrice < (optimizingProduct.price || 0) ? (
                      <span className="text-red-400">
                        Reducing by ${((optimizingProduct.price || 0) - newOptimizedPrice).toFixed(2)} ({(((optimizingProduct.price || 0) - newOptimizedPrice) / (optimizingProduct.price || 1) * 100).toFixed(0)}% off)
                      </span>
                    ) : (
                      <span className="text-green-400">
                        Increasing by ${(newOptimizedPrice - (optimizingProduct.price || 0)).toFixed(2)}
                      </span>
                    )}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowOptimizeModal(false); setOptimizingProduct(null); }}
                  className="flex-1 px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors font-medium"
                  data-testid="optimize-cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                    const originalPrice = optimizingProduct.msrp || optimizingProduct.price || 100;
                    const currentPrice = optimizingProduct.price || 0;
                    
                    // Close modal first to prevent blank page
                    setShowOptimizeModal(false);
                    
                    try {
                      // Only create a deal if it's a discount (price reduction)
                      let dealCreated = false; // F-M02-4: success is only claimed when a deal was actually created
                      if (newOptimizedPrice < originalPrice) {
                        const discountValue = ((originalPrice - newOptimizedPrice) / originalPrice) * 100;
                        
                        await dashboardService.createDeal({
                          product_id: optimizingProduct._id,
                          discount_type: 'percentage',
                          discount_value: Math.max(0, Math.round(discountValue * 100) / 100),
                          coupon_code: null,
                          end_date: null
                        });
                        dealCreated = true;
                      }
                      
                      // Mark action as completed if it came from NBA
                      if (optimizingProduct._actionId) {
                        markActionCompleted(optimizingProduct._actionId);
                      }
                      
                      // Original: toast.success('Price optimized successfully!', ...) shown even when no deal was created.
                      if (dealCreated) {
                        toast.success('Deal created at the optimized price', {
                          description: `New price: $${newOptimizedPrice.toFixed(2)}`
                        });
                      } else {
                        toast.info('No deal created', {
                          description: 'The new price is not below the list price.'
                        });
                      }
                      
                      // Refresh products
                      try {
                        const productsData = await dashboardService.getProducts(50, 0);
                        setProducts(productsData.products || []);
                        generateNextBestActions(productsData.products || []);
                      } catch (refreshError) {
                        console.error('Failed to refresh products:', refreshError);
                      }
                    } catch (error) {
                      console.error('Failed to optimize price:', error);
                      toast.error('Failed to optimize price', {
                        description: error.response?.data?.detail || 'Please try again'
                      });
                    } finally {
                      setOptimizingProduct(null);
                    }
                  }}
                  disabled={newOptimizedPrice === (optimizingProduct.price || 0)}
                  className="flex-1 px-4 py-3 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-bold hover:bg-[#3EF1C7]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="optimize-confirm-btn"
                >
                  Confirm New Price
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Price Modal - With Slider */}
      {showAdjustPriceModal && adjustingProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex: 9999}} data-testid="adjust-price-modal">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-lg shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.75)]">
            <div className="p-6 border-b border-[#22304A]">
              <h3 className="text-xl font-semibold text-white">Adjust Pricing</h3>
              <p className="text-sm text-[#9CB0C7] mt-1">{adjustingProduct.title}</p>
            </div>
            
            <div className="p-6">
              {/* Price Summary */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-[#111A2E] rounded-lg p-3 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Original Price</p>
                  <p className="text-xl font-bold text-white">${(adjustingProduct.msrp || adjustingProduct.price || 0).toFixed(2)}</p>
                </div>
                <div className="bg-[#111A2E] rounded-lg p-3 border border-[#22304A]">
                  <p className="text-xs text-[#9CB0C7] mb-1">Current Price</p>
                  <p className="text-xl font-bold text-[#FFC857]">${(adjustingProduct.price || 0).toFixed(2)}</p>
                </div>
                <div className="bg-[#3EF1C7]/10 rounded-lg p-3 border border-[#3EF1C7]/30">
                  <p className="text-xs text-[#3EF1C7] mb-1">New Price</p>
                  <p className="text-xl font-bold text-[#3EF1C7]" id="adjustNewPriceDisplay">${(adjustingProduct.price || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* CI Score Display */}
              <div className="bg-[#111A2E] rounded-lg p-4 mb-6 border border-[#22304A]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#9CB0C7]">Current CI Score</p>
                    <p className="text-2xl font-bold text-white">{adjustingProduct.ci_score || 500}</p>
                  </div>
                  <div className="text-2xl text-[#9CB0C7]">→</div>
                  <div className="text-right">
                    <p className="text-xs text-[#3EF1C7]">Projected CI Score</p>
                    <p className="text-2xl font-bold text-[#8CF78E]" id="adjustProjectedScore">{Math.min(950, (adjustingProduct.ci_score || 500) + 150)}</p>
                  </div>
                </div>
              </div>

              {/* Price Slider */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-3">Adjust Price</label>
                <input
                  type="range"
                  min={Math.max(0, (adjustingProduct.price || 100) * 0.5)}
                  max={(adjustingProduct.msrp || adjustingProduct.price || 100) * 1.1}
                  step={0.01}
                  defaultValue={adjustingProduct.price || 0}
                  onChange={(e) => {
                    const newPrice = parseFloat(e.target.value);
                    const originalPrice = adjustingProduct.msrp || adjustingProduct.price || 100;
                    const discount = ((originalPrice - newPrice) / originalPrice) * 100;
                    const baseScore = adjustingProduct.ci_score || 500;
                    const projectedScore = Math.min(950, Math.round(baseScore + (discount * 5)));
                    
                    document.getElementById('adjustNewPriceDisplay').textContent = `$${newPrice.toFixed(2)}`;
                    document.getElementById('adjustProjectedScore').textContent = projectedScore;
                    document.getElementById('adjustDiscountDisplay').textContent = `${discount.toFixed(0)}% off`;
                    document.getElementById('adjustNewPriceInput').value = newPrice.toFixed(2);
                  }}
                  className="w-full h-2 bg-[#22304A] rounded-lg appearance-none cursor-pointer accent-[#3EF1C7]"
                  style={{
                    background: `linear-gradient(to right, #3EF1C7 0%, #3EF1C7 50%, #22304A 50%, #22304A 100%)`
                  }}
                />
                <div className="flex justify-between mt-2 text-xs text-[#9CB0C7]">
                  <span>${(Math.max(0, (adjustingProduct.price || 100) * 0.5)).toFixed(2)}</span>
                  <span id="adjustDiscountDisplay">0% off</span>
                  <span>${((adjustingProduct.msrp || adjustingProduct.price || 100) * 1.1).toFixed(2)}</span>
                </div>
              </div>

              {/* Manual Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Or enter exact price</label>
                <div className="relative max-w-xs">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CB0C7]">$</span>
                  <input
                    id="adjustNewPriceInput"
                    type="number"
                    defaultValue={(adjustingProduct.price || 0).toFixed(2)}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white text-lg font-bold focus:outline-none focus:border-[#3EF1C7]"
                    onChange={(e) => {
                      const newPrice = parseFloat(e.target.value) || 0;
                      const originalPrice = adjustingProduct.msrp || adjustingProduct.price || 100;
                      const discount = ((originalPrice - newPrice) / originalPrice) * 100;
                      const baseScore = adjustingProduct.ci_score || 500;
                      const projectedScore = Math.min(950, Math.round(baseScore + (discount * 5)));
                      
                      document.getElementById('adjustNewPriceDisplay').textContent = `$${newPrice.toFixed(2)}`;
                      document.getElementById('adjustProjectedScore').textContent = projectedScore;
                      document.getElementById('adjustDiscountDisplay').textContent = `${discount.toFixed(0)}% off`;
                    }}
                  />
                </div>
              </div>

              {/* AI Recommendation */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-300 mb-1">💡 AI Recommendation</p>
                <p className="text-xs text-[#9CB0C7]">
                  Reduce price by <span className="font-bold text-white">15%</span> to reach optimal CI Score of <span className="font-bold text-[#8CF78E]">850</span> and increase conversions by ~25%
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button 
                  onClick={() => { setShowAdjustPriceModal(false); setAdjustingProduct(null); }}
                  className="flex-1 px-4 py-3 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                    const newPrice = parseFloat(document.getElementById('adjustNewPriceInput').value) || adjustingProduct.price;
                    const originalPrice = adjustingProduct.msrp || adjustingProduct.price || 100;
                    const discountValue = ((originalPrice - newPrice) / originalPrice) * 100;
                    
                    // Close modal first to prevent blank page
                    setShowAdjustPriceModal(false);
                    
                    try {
                      // Only create a deal if it's a discount (price reduction)
                      if (newPrice < originalPrice) {
                        await dashboardService.createDeal({
                          product_id: adjustingProduct._id,
                          discount_type: 'percentage',
                          discount_value: Math.max(0, Math.round(discountValue * 100) / 100),
                          coupon_code: null,
                          end_date: null
                        });
                      }
                      
                      // Mark action as completed if it came from NBA
                      if (adjustingProduct._actionId) {
                        markActionCompleted(adjustingProduct._actionId);
                      }
                      
                      toast.success('Price adjusted successfully!', {
                        description: `New price: $${newPrice.toFixed(2)}${discountValue > 0 ? ` (${discountValue.toFixed(0)}% off)` : ''}`
                      });
                      
                      // Refresh products
                      try {
                        const productsData = await dashboardService.getProducts(50, 0);
                        setProducts(productsData.products || []);
                        generateNextBestActions(productsData.products || []);
                      } catch (refreshError) {
                        console.error('Failed to refresh products:', refreshError);
                      }
                    } catch (error) {
                      console.error('Failed to adjust price:', error);
                      toast.error('Failed to adjust price', {
                        description: error.response?.data?.detail || 'Please try again'
                      });
                    } finally {
                      setAdjustingProduct(null);
                    }
                  }}
                  className="flex-1 px-4 py-3 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-bold hover:bg-[#3EF1C7]/90 transition-colors"
                >
                  Apply New Price
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CI Score Explainer Modal */}
      {showCIExplainer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426] w-full max-w-3xl shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.75)] max-h-[90vh] overflow-y-auto">
            <CIScoreExplainer showButton={false} />
            <div className="p-6 text-center border-t border-[#22304A]">
              <button
                onClick={() => setShowCIExplainer(false)}
                className="px-6 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 font-medium"
                data-testid="close-ci-explainer-button"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
